import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin, useRequiredParams } from '@piccolohealth/ui';
import React from 'react';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { TabsRouter } from '../../components/generic/TabsRouter';
import { ContentLayout } from '../../components/layouts/ContentLayout';
import { useUserQuery } from '../../graphql/hooks/useUserQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { UserBasic } from './UserBasic';
import { UserParticipants } from './UserParticipants';

export const User = () => {
  const { userId } = useRequiredParams<{ userId: string }>();
  const { organization } = useAppContext();

  const { data, error, isLoading } = useUserQuery({
    organizationId: organization.id,
    userId,
  });

  const user = data?.organization?.user as Common.User | null;

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  if (!user) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'UserNotFound',
            message: 'User not found',
          })
        }
      />
    );
  }

  const routes = ['basic', 'participants'];

  return (
    <ContentLayout
      headerContent={
        <ChakraV3.Heading size='3xl' fontWeight='bold'>
          {user.name}
        </ChakraV3.Heading>
      }
    >
      <TabsRouter
        variant='subtle'
        orientation='vertical'
        defaultValue={routes[0]}
        size='sm'
        w='full'
        h='full'
        lazyMount
        unmountOnExit
        routes={routes}
      >
        <ChakraV3.Tabs.List minW='120px' flexShrink={0} py={2} ml={6}>
          <ChakraV3.Tabs.Trigger value='basic'>Basic</ChakraV3.Tabs.Trigger>
          <ChakraV3.Tabs.Trigger value='participants'>Participants</ChakraV3.Tabs.Trigger>
        </ChakraV3.Tabs.List>
        <ChakraV3.Tabs.ContentGroup overflowY='auto' h='full' w='full' px={6} py={2}>
          <ChakraV3.Tabs.Content value='basic'>
            <UserBasic user={user} />
          </ChakraV3.Tabs.Content>
          <ChakraV3.Tabs.Content value='participants'>
            <UserParticipants user={user} />
          </ChakraV3.Tabs.Content>
        </ChakraV3.Tabs.ContentGroup>
      </TabsRouter>
    </ContentLayout>
  );
};
